import React, { useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { GAMES } from "../config/games";
import CreatorMessage from "../components/CreatorMessage";
import "../styles/HomePage.css";
import "../styles/CreatorMessage.css";

const GAME_COVERS = {
  "spells":    "/game-covers/spells.webp",
  "locations": "/game-covers/locations.webp",
  "npcs":      "/game-covers/npc.webp",
  "lore":      "/game-covers/lore.webp",
};

const GAME_META = {
  "guess-spell": {
    desc: "Name the spell from partial tooltips, school, and cast-time clues.",
  },
  "guess-location": {
    desc: "Pinpoint the zone from a screenshot. How well do you know the map?",
  },
  "guess-npc": {
    desc: "An NPC hides in Azeroth — figure out who from faction and location clues.",
  },
  "guess-lore": {
    desc: "Test your deep lore knowledge with riddles from Warcraft history.",
  },
};

const QUICK_LINKS = [
  { key: "leaderboard", path: "/leaderboard", title: "Leaderboard", desc: "Top players & daily rankings" },
  { key: "feedback",    path: "/feedback",    title: "Feedback",    desc: "Bugs, ideas & suggestions" },
  { key: "profile",     path: "/profile",     title: "Profile",     desc: "Stats, streaks & achievements" },
];

/* ── Scroll-reveal hook ── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add("revealed"); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = "", style, ...rest }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal-item ${className}`} style={style} {...rest}>
      {children}
    </div>
  );
}

/* ================================================================ */

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="page-bg home-root">

      {/* Noise texture overlay */}
      <div className="home-noise" aria-hidden="true" />

      {/* Ambient floating orbs */}
      <div className="home-orb home-orb--1" aria-hidden="true" />
      <div className="home-orb home-orb--2" aria-hidden="true" />
      <div className="home-orb home-orb--3" aria-hidden="true" />

      {/* ═══════ HERO ═══════ */}
      <header className="home-hero">
        <p className="home-hero-eyebrow">
          <span className="home-eyebrow-dash" />
          World of Warcraft · Daily Challenge
          <span className="home-eyebrow-dash" />
        </p>
        <h1 className="home-hero-title">AZERODLE</h1>
        <p className="home-hero-sub">Test your knowledge of Azeroth</p>
      </header>

      {/* ═══════ WIADOMOŚĆ OD TWÓRCY — dane pobierane z API ═══════ */}
      <div className="home-section">
        <CreatorMessage />
      </div>

      {/* ═══════ GAME MODES ═══════ */}
      <section className="home-section">
        <div className="home-section-header">
          <span className="home-section-line home-section-line--left" />
          <span className="home-section-label">Game Modes</span>
          <span className="home-section-line home-section-line--right" />
        </div>

        <div className="home-tiles">
          {GAMES.map((game, idx) => {
            const meta = GAME_META[game.key] || {};
            const bgUrl = GAME_COVERS[game.key];
            return (
              <Reveal
                key={game.key}
                className="home-tile"
                style={{ "--i": idx }}
                onClick={() => navigate(game.path)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && navigate(game.path)}
              >
                <div className="home-tile-bg" style={{ backgroundImage: `url(${bgUrl})` }} />
                <div className="home-tile-overlay" />
                <div className="home-tile-shimmer" />
                <div className="home-tile-body">
                  <h3 className="home-tile-title">{game.title}</h3>
                  <p className="home-tile-desc">{meta.desc || game.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ═══════ QUICK LINKS ═══════ */}
      <section className="home-section home-section--links">
        <div className="home-section-header">
          <span className="home-section-line home-section-line--left" />
          <span className="home-section-label">Explore</span>
          <span className="home-section-line home-section-line--right" />
        </div>

        <div className="home-links">
          {QUICK_LINKS.map((link, idx) => (
            <Reveal key={link.key} className="home-qlink" style={{ "--i": idx }}>
              <Link to={link.path} className="home-qlink-inner">
                <div className="home-qlink-text">
                  <span className="home-qlink-title">{link.title}</span>
                  <span className="home-qlink-desc">{link.desc}</span>
                </div>
                <span className="home-qlink-arrow">&#8250;</span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}